/**
 * drawerTelemetry — three stable, greppable tokens for the reworked running
 * drawer, emitted from SHARED code so both hosts record them identically.
 *
 * There is no browser telemetry pipe in this repo: `ui/app/appInsights.ts` is
 * the only bridge and it is SERVER-side, so a task-pane record is console-only
 * today. The tokens exist so the RATE is greppable in a captured console log
 * rather than guessed — and so a host that grows a sink can forward them
 * without a second logging path appearing.
 *
 * Rules this module enforces rather than documents:
 *   - de-duplicated per (token, instance_id, cause), so a render loop turns one
 *     fallback into ONE record and not a thousand;
 *   - never throws and never blocks a render — a failing sink is swallowed;
 *   - carries no PII beyond ids already on the surface: no mailbox content, no
 *     credential, no token.
 */

export type DrawerTelemetryToken =
  /** A pre-model / undeclared instance rendered the honest fallback. */
  | "emuw_phase_model_fallback"
  /** A branch submit was refused by the server. */
  | "emuw_evidence_submit_rejected"
  /** The take-over branch was withdrawn because the run had resumed. */
  | "emuw_evidence_already_resumed";

export interface DrawerTelemetryRecord {
  token: DrawerTelemetryToken;
  instance_id: string | null;
  workflow_id: string | null;
  /** Short, non-PII discriminator — a source name, a status code, a branch. */
  detail: string | null;
}

export type DrawerTelemetrySink = (record: DrawerTelemetryRecord) => void;

let sink: DrawerTelemetrySink | null = null;

/** A host may forward records somewhere real. Optional by design. */
export function setDrawerTelemetrySink(next: DrawerTelemetrySink | null): void {
  sink = next;
}

const seen = new Set<string>();

/** Test seam. Never called from production code. */
export function resetDrawerTelemetry(): void {
  seen.clear();
  sink = null;
}

export function emitDrawerTelemetry(
  token: DrawerTelemetryToken,
  args: {
    instanceId?: string | null;
    workflowId?: string | null;
    detail?: string | null;
  } = {},
): void {
  try {
    const record: DrawerTelemetryRecord = {
      token,
      instance_id: args.instanceId ?? null,
      workflow_id: args.workflowId ?? null,
      detail: args.detail ?? null,
    };
    const key = `${token}|${record.instance_id ?? ""}|${record.detail ?? ""}`;
    if (seen.has(key)) return;
    seen.add(key);
    if (sink) {
      sink(record);
    } else {
      console.warn("[emuw]", record);
    }
  } catch {
    // Telemetry must never break a render. Deliberately silent.
  }
}

export default emitDrawerTelemetry;
